import { Component, OnInit, ViewChild, Input, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { GestureController, Gesture, ModalController, GestureDetail } from '@ionic/angular';

@Component({
  selector: 'app-modal-open-fullimage',
  templateUrl: './modal-open-fullimage.component.html',
  styleUrls: ['./modal-open-fullimage.component.scss'],
})
export class ModalOpenFullimageComponent implements OnInit, AfterViewInit {
  @ViewChild('image', { read: ElementRef }) image: ElementRef<HTMLIonImgElement>;
  @Input() useCloseBtn = false;
  @Input() imgAlert: string;

  zoom = 1;

  private offsetX = 0;
  private offsetY = 0;
  private lastOffsetX = 0;
  private lastOffsetY = 0;

  private lastOnStart = 0;
  private DOUBLE_CLICK_THRESHOLD = 500;

  constructor(
    private modalController: ModalController,
    private gestureController: GestureController,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    const swipe: Gesture = this.gestureController.create(
      {
        el: this.image.nativeElement,
        threshold: 0,
        gestureName: 'swipe',
        gesturePriority: 1,
        onStart: detail => this.onSwipeStart(detail),
        onMove: detail => this.onSwipe(detail),
        onEnd: () => this.onSwipeEnd(),
      },
      true,
    );

    swipe.enable();
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  confirm() {
    this.modalController.dismiss(null, 'confirm');
  }

  private onSwipeStart(detail: GestureDetail) {
    const { deltaX, deltaY } = detail;

    if (deltaX === 0 && deltaY === 0) {
      this.onDoubleClickStart();
    }

    this.cdRef.detectChanges();
  }

  private onSwipeEnd() {
    this.adjustMaxOffset();

    this.lastOffsetX = this.offsetX;
    this.lastOffsetY = this.offsetY;

    this.cdRef.detectChanges();
  }

  private onSwipe(detail: GestureDetail) {
    const { deltaX, deltaY } = detail;

    if (this.zoom === 1) {
      return;
    }

    this.image.nativeElement.style.setProperty('transform', this.getNewTransform(deltaX, deltaY));
  }

  private onDoubleClickStart() {
    const now = Date.now();

    if (Math.abs(now - this.lastOnStart) <= this.DOUBLE_CLICK_THRESHOLD) {
      this.image.nativeElement.style.setProperty('scale', this.getNewScale());
      this.lastOnStart = 0;
    } else {
      this.lastOnStart = now;
    }
  }

  private adjustMaxOffset() {
    const imgWidth = this.image.nativeElement.clientWidth;
    const imgHeight = this.image.nativeElement.clientHeight;

    const windowHeight = window.innerHeight;

    let maxOffsetX = 0;
    const maxOffsetY = (windowHeight - imgHeight * this.zoom) / (2 * this.zoom);

    switch (this.zoom) {
      case 2:
        maxOffsetX = imgWidth * 0.25;
        break;
      case 3:
        maxOffsetX = imgWidth * 0.33;
        break;
      default:
        maxOffsetX = 0;
        break;
    }

    const applyAdjustment = () => {
      this.image.nativeElement.style.setProperty('transform', this.getNewTransform());
      this.image.nativeElement.style.setProperty('transition', 'all 300ms ease');

      setTimeout(() => this.image.nativeElement.style.removeProperty('transition'), 300);
    };

    if (this.offsetX > maxOffsetX) {
      this.offsetX = maxOffsetX;
      applyAdjustment();
    }

    if (this.offsetX < -1 * maxOffsetX) {
      this.offsetX = -1 * maxOffsetX;
      applyAdjustment();
    }

    if (this.offsetY > maxOffsetY) {
      this.offsetY = maxOffsetY;
      applyAdjustment();
    }

    if (this.offsetY < -1 * maxOffsetY) {
      this.offsetY = -1 * maxOffsetY;
      applyAdjustment();
    }
  }

  private getNewScale() {
    if (this.zoom >= 3) {
      this.resetZoom();
    } else {
      this.zoom++;
    }

    return `${this.zoom}`;
  }

  private getNewTransform(deltaX?: number, deltaY?: number) {
    if (deltaX && deltaY) {
      this.offsetX = this.lastOffsetX + deltaX;
      this.offsetY = this.lastOffsetY + deltaY;
    }

    return `translate(${this.offsetX}px, ${this.offsetY}px)`;
  }

  private resetZoom() {
    this.zoom = 1;
    this.image.nativeElement.style.setProperty('transform', `translate(0px, 0px)`);
    this.offsetX = 0;
    this.offsetY = 0;
    this.lastOffsetX = 0;
    this.lastOffsetY = 0;
  }
}
